import useGetMetadata from "customHooks/useGetMetadata";
import { useTranslation } from "react-i18next";

export interface ExportDbField {
    id: string;
    label: string;
    arrayIndex?: number;
    fullDef: Compleo.IObject;
}

const fieldsToIgnore = ["location"];
const manualAddressFields: string[] = ["address"];
const manualExportFieldsGeneral: string[] = [
    "createdAt",
    "lastUpdatedAt",
    "applicantId"
];
const manualExportFieldsJobContext: string[] = [
    "createdAt",
    "lastUpdatedAt",
    "applicantId",
    "screeningQuestionnaire",
    "pipelineStage"
];

function buildManualAddressFields(
    fields: string[],
    baseDefinition: Compleo.IObject | undefined,
    translateApplicant: (value: string) => string,
    translateAddress: (value: string) => string
): ExportDbField[] {
    const addressLabel =
        translateApplicant("address") || translateAddress("address");

    return fields.map((fieldName) => {
        const baseFullDef: Compleo.IObject = baseDefinition
            ? { ...baseDefinition, fieldName }
            : ({ fieldName, elementType: "address" } as Compleo.IObject);

        return {
            id: fieldName,
            label: addressLabel || fieldName,
            fullDef: baseFullDef
        };
    });
}

export function useExportSettings(isJobContext: boolean = false) {
    const moduleName = "APPLICANTNEW";
    const [tApplicant, , readyApplicant] = useTranslation(moduleName, {
        useSuspense: false
    });
    const [tAddress, , readyAddress] = useTranslation("ADDRESSFIELD", {
        useSuspense: false
    });
    const [tJob, , readyJob] = useTranslation("JOBNEW", {
        useSuspense: false
    });

    const [metadata] = useGetMetadata(moduleName);

    const ready =
        readyApplicant &&
        readyAddress &&
        metadata.status === "success" &&
        (!isJobContext || readyJob);

    const manualExportFields = isJobContext
        ? manualExportFieldsJobContext
        : manualExportFieldsGeneral;

    const listas = metadata?.response?.data?.listas || {};
    let dbFields: ExportDbField[] = [];

    if (ready) {
        const metadataFields = metadata?.response?.data?.metadado || [];
        const complexFieldDefinitions = metadataFields.filter(
            (item: Compleo.IObject) => item.elementType === "complexField"
        );
        const complexFieldNames = complexFieldDefinitions.map(
            (item: Compleo.IObject) => item.fieldName
        );

        dbFields = metadataFields
            .filter((item: Compleo.IObject) => {
                const elementType = item.elementType || "";
                return (
                    ![
                        "complexField",
                        "hidden",
                        "file",
                        "multipleFiles",
                        "image"
                    ].includes(elementType) &&
                    !fieldsToIgnore.includes(item.fieldName) &&
                    !elementType.includes("backend") &&
                    !complexFieldNames.some((complexFieldName: string) =>
                        item.fieldName.includes(`${complexFieldName}_`)
                    )
                );
            })
            .map((field: Compleo.IObject) => ({
                id: field.fieldName,
                label: tApplicant(field.fieldName),
                fullDef: field
            }));

        for (const complexFieldDefinition of complexFieldDefinitions) {
            if (
                !dbFields.some(
                    (field) => field.id === complexFieldDefinition.fieldName
                )
            ) {
                dbFields.push({
                    id: complexFieldDefinition.fieldName,
                    label: tApplicant(complexFieldDefinition.fieldName),
                    fullDef: complexFieldDefinition
                });
            }
        }

        const locationDefinition = metadataFields.find(
            (item: Compleo.IObject) => item.fieldName === "location"
        );

        const manualFields = buildManualAddressFields(
            manualAddressFields,
            locationDefinition,
            tApplicant,
            tAddress
        );

        for (const manualField of manualFields) {
            if (!dbFields.some((field) => field.id === manualField.id)) {
                dbFields.push(manualField);
            }
        }

        // Create merged translation function for job context fields
        const getFieldLabel = (fieldName: string): string => {
            if (isJobContext) {
                // For job context, try JOBNEW first for job-related fields
                if (
                    fieldName === "screeningQuestionnaire" ||
                    fieldName === "pipelineStage"
                ) {
                    const jobLabel = tJob(fieldName);
                    if (jobLabel && jobLabel !== fieldName) {
                        return jobLabel;
                    }
                }
            }
            // Fallback to APPLICANTNEW
            return tApplicant(fieldName);
        };

        for (const fieldName of manualExportFields) {
            if (dbFields.some((field) => field.id === fieldName)) {
                continue;
            }
            const backendField = metadataFields.find(
                (item: Compleo.IObject) => item.fieldName === fieldName
            );
            const derivedDefinition: Compleo.IObject = backendField
                ? { ...backendField, fieldName }
                : ({ fieldName, elementType: "text" } as Compleo.IObject);

            dbFields.push({
                id: fieldName,
                label: getFieldLabel(fieldName),
                fullDef: derivedDefinition
            });
        }
    }

    function getListFromField(field?: string) {
        if (!field) {
            return [];
        }
        let normalizedField = field;
        if (normalizedField.includes("COMPLEXFIELD:")) {
            normalizedField = normalizedField.split("-")[0];
        }
        return listas[normalizedField] || [];
    }

    return { ready, dbFields, getListFromField, t: tApplicant };
}
